import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdjustmentsIcon, HeartIcon, OfficeBuildingIcon } from "@heroicons/react/solid";
import { BACKEND_URL } from "../config";
import { useAuth } from "../hooks/useAuth";

const emptyPrefs = {
  interestedCountries: [],
  preferredCourses: [],
  budget: "",
  studyPlan: "",
};

function asList(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") return value.split(",").map((item) => item.trim()).filter(Boolean);
  return [];
}

export default function StudentActivity() {
  const { token } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [preferences, setPreferences] = useState(emptyPrefs);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    async function loadActivity() {
      if (!token) return;
      setLoading(true);
      try {
        const [wishlistRes, prefsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/wishlist`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${BACKEND_URL}/student/preferences`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!alive) return;

        if (wishlistRes.ok) {
          const saved = await wishlistRes.json();
          setWishlist(Array.isArray(saved) ? saved : []);
        }

        if (prefsRes.ok) {
          const prefs = await prefsRes.json();
          setPreferences({
            interestedCountries: asList(prefs.interestedCountries),
            preferredCourses: asList(prefs.preferredCourses),
            budget: prefs.budget || "",
            studyPlan: prefs.studyPlan || "",
          });
        }
      } catch (err) {
        console.error("Student activity load error", err);
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadActivity();
    return () => {
      alive = false;
    };
  }, [token]);

  const preferenceCount = useMemo(
    () =>
      [
        asList(preferences.interestedCountries).length,
        asList(preferences.preferredCourses).length,
        preferences.budget,
        preferences.studyPlan,
      ].filter(Boolean).length,
    [preferences]
  );

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-8 text-white sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[4px] text-cyan-200">My Activity</p>
          <h1 className="mt-3 text-3xl font-semibold">Your AbroadJacks activity</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
            Track saved consultancies and account setup progress using your existing student data.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-5">
            <HeartIcon className="h-6 w-6 text-rose-200" />
            <p className="mt-4 text-3xl font-semibold">{wishlist.length}</p>
            <p className="text-sm text-slate-400">Saved consultancies</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-5">
            <AdjustmentsIcon className="h-6 w-6 text-cyan-200" />
            <p className="mt-4 text-3xl font-semibold">{preferenceCount}/4</p>
            <p className="text-sm text-slate-400">Preference areas completed</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-5">
            <OfficeBuildingIcon className="h-6 w-6 text-emerald-200" />
            <p className="mt-4 text-3xl font-semibold">-</p>
            <p className="text-sm text-slate-400">Enquiry history unavailable</p>
          </div>
        </div>

        <section className="mt-6 rounded-2xl border border-white/10 bg-white/[0.06] p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Recent saved consultancies</h2>
            <Link to="/wishlist" className="text-sm font-semibold text-cyan-200 hover:text-cyan-100">
              Open wishlist
            </Link>
          </div>
          {loading ? (
            <p className="text-sm text-slate-400">Loading activity...</p>
          ) : wishlist.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 p-5 text-sm text-slate-300">
              No saved consultancies yet. Browse the marketplace and save profiles you want to compare.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {wishlist.slice(0, 6).map((item) => (
                <Link
                  key={item._id}
                  to={`/consultancy/${item.consultancy?._id}`}
                  className="rounded-xl border border-white/10 bg-slate-950/45 p-4 transition hover:border-cyan-200/40"
                >
                  <p className="line-clamp-1 font-semibold">{item.consultancy?.name || "Consultancy"}</p>
                  <p className="mt-1 text-sm text-slate-400">
                    {[item.consultancy?.city, item.consultancy?.state].filter(Boolean).join(", ") || "Open profile"}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="mt-6 rounded-2xl border border-white/10 bg-white/[0.06] p-5">
          <h2 className="text-lg font-semibold">Connection history</h2>
          <p className="mt-2 text-sm leading-6 text-slate-300">
            Student enquiry tracking is not exposed by the current backend API. Saved consultancies and preferences are shown here using existing student endpoints.
          </p>
        </section>
      </div>
    </main>
  );
}
